import { supabase } from '../utils/supabase';
import { LEADERBOARD_SCOPES } from '../utils/leaderboardPlayerScope';
import {
  buildMovementMapForScope,
  collectPlayerIdsFromRecentSnapshots,
  snapshotFetchLimitForScope,
} from '../utils/leaderboardSnapshotUtils';

/**
 * Fetches recent snapshot headers + entries for movement under the given scope.
 *
 * @param {string} clubId
 * @param {string} seasonId
 * @param {string} ladderType
 * @param {'active'|'all'} scope
 */
export const fetchRecentSnapshotsForScope = async (clubId, seasonId, ladderType, scope) => {
  const limit = snapshotFetchLimitForScope(scope);
  const { data: headers, error: headersError } = await supabase
    .from('leaderboard_snapshots')
    .select('id, snapshot_at')
    .eq('club_id', clubId)
    .eq('season_id', seasonId)
    .eq('ladder_type', ladderType)
    .order('snapshot_at', { ascending: false })
    .limit(limit);

  if (headersError) throw headersError;
  if (!headers || headers.length === 0) {
    return { current: null, previous: null };
  }

  const snapshotIds = headers.map((h) => h.id);
  const { data: entries, error: entriesError } = await supabase
    .from('leaderboard_snapshot_entries')
    .select('snapshot_id, player_id, elo_rating, rank, rank_delta, elo_delta')
    .in('snapshot_id', snapshotIds);

  if (entriesError) throw entriesError;

  const entriesBySnapshotId = Object.fromEntries(snapshotIds.map((id) => [id, []]));
  (entries ?? []).forEach((entry) => {
    if (entriesBySnapshotId[entry.snapshot_id]) {
      entriesBySnapshotId[entry.snapshot_id].push(entry);
    }
  });

  const buildSnapshot = (header) =>
    header
      ? {
          snapshot_at: header.snapshot_at,
          entries: entriesBySnapshotId[header.id] ?? [],
        }
      : null;

  return {
    current: buildSnapshot(headers[0]),
    previous: buildSnapshot(headers[1] ?? null),
  };
};

/**
 * Loads movement maps for multiple ladders using deduped recent-snapshot fetches.
 *
 * @param {Object} params
 * @param {string} params.clubId
 * @param {string} params.seasonId
 * @param {'active'|'all'} params.scope
 * @param {Array<{ key: string, ladderType: string }>} params.ladders
 * @param {Record<string, string[]>} params.sessionDatesByPlayerId
 * @returns {Promise<Record<string, Map>>}
 */
export const fetchLeaderboardMovementMaps = async ({
  clubId,
  seasonId,
  scope,
  ladders,
  sessionDatesByPlayerId = {},
}) => {
  const recentSnapshotsList = await Promise.all(
    ladders.map(({ ladderType }) =>
      fetchRecentSnapshotsForScope(clubId, seasonId, ladderType, scope).catch(() => null)
    )
  );

  const movementState = {};
  ladders.forEach(({ key }, index) => {
    movementState[key] = buildMovementMapForScope(
      scope,
      recentSnapshotsList[index],
      sessionDatesByPlayerId
    );
  });

  return { movementState, recentSnapshotsList };
};

export { collectPlayerIdsFromRecentSnapshots, snapshotFetchLimitForScope, LEADERBOARD_SCOPES };
